import { Core } from "@faire/design-tokens";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { getBrand } from "@faire/retailer-visitor-shared/serialized-data/getBrand";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackSignupSignUpModalSignInRedirectClick } from "@faire/web-api--source/events/signup/click/signUpModalSignInRedirect";
import { QueryParameters as SignUpQueryParameters } from "@faire/web-api--source/routes/www/welcome/r/personal";
import React, { useCallback, type JSX } from "react";
import styled from "styled-components";

export const LongSignInLink: React.FC = () => {
  const linkFormatting = useCallback(
    (chunks: JSX.Element) => <SignInLinkButton>{chunks}</SignInLinkButton>,
    []
  );

  return (
    <>
      <Spacer height="3x" />
      <Typography align="center">
        <StrictLocalMsg
          defaultMessage="Already have an account? <linkFormatting>Sign in</linkFormatting>"
          description={{
            text: "Give the user the option to Sign in to an existing account",
            img: "https://cdn.faire.com/fastly/5fc414ae812fb579beaccafcb87cad84068381bf2b57b6ddaceacc71e920ea6f.png",
          }}
          values={{
            linkFormatting,
          }}
        />
      </Typography>
      <Spacer height="3x" />
    </>
  );
};

export const ShortSignInLink = () => {
  const isBrandPreview = getIsBrandPreview();
  const brand = getBrand();

  if (isBrandPreview) {
    return (
      <>
        <FormSpacer height="2x" />
        <Typography color={Core.text.critical}>
          <StrictLocalMsg
            defaultMessage="You’re already signed in as {brandName}."
            description={{
              text: "Context as to why the sign up button is disabled",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              brandName: brand?.name,
            })}
          />
        </Typography>
      </>
    );
  }
  return (
    <>
      <FormSpacer height="2x" />
      <SignInLinkButton>
        <StrictLocalMsg
          defaultMessage="Sign in"
          description={{
            text: "Give the user the option to Sign in to an existing account",
            img: "https://cdn.faire.com/fastly/b4af2f5790b4e0752944ac056de2906b679cd6fd241b5101f29e827cc2523f2f.png",
          }}
        />
      </SignInLinkButton>
    </>
  );
};

const FormSpacer = styled(Spacer)`
  ${responsiveMediaQueries.mobile} {
    flex-basis: ${getSpacing("2x")};
    min-height: ${getSpacing("2x")};
  }
`;

const SignInLinkButton: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const signUpParams = useQueryParams(SignUpQueryParameters.parse);

  return (
    <Button
      variant="tertiary"
      linkTo={getCurrentPath()
        .delete("signUp")
        .append({ signIn: signUpParams.signUp ?? "1" })
        .toString()}
      onClick={trackSignupSignUpModalSignInRedirectClick}
    >
      {children}
    </Button>
  );
};
